import type { ColumnsType } from "antd/es/table";

import AvatarWithInitials from "@/components/UI/AvatarWithInitials";
import type { PlanOfRecordMilestoneRow } from "./planOfRecord.types";

export interface MilestoneHistoryRow {
  id: string;
  milestoneLabel: string;
  event: "Created" | "Last modified";
  at: string;
  by: string | null;
  reason: string | null;
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/**
 * created_at (auto_now_add) and last_modified_at (auto_now) are each stamped by their own
 * timezone.now() call during the same save() — they differ by milliseconds on a row that
 * has never been touched since creation. Without this tolerance, every untouched milestone
 * spawns a phantom "Last modified" row identical in substance to its "Created" one.
 */
const SAME_SAVE_TOLERANCE_MS = 5000;

function isGenuineEdit(row: PlanOfRecordMilestoneRow): boolean {
  if (!row.last_modified_at) return false;
  const gapMs = Math.abs(
    new Date(row.last_modified_at).getTime() -
      new Date(row.created_at).getTime(),
  );
  return gapMs > SAME_SAVE_TOLERANCE_MS;
}

// Reduced to the two facts the backend actually carries (created, last modified) until a
// dataset exists over the PlanOfRecordMilestoneEvent pghistory table for full field-level diffs.
export function buildMilestoneHistoryRows(
  rows: PlanOfRecordMilestoneRow[],
): MilestoneHistoryRow[] {
  const history = rows.flatMap((row): MilestoneHistoryRow[] => {
    const entries: MilestoneHistoryRow[] = [
      {
        id: `${row.id}-created`,
        milestoneLabel: row.label,
        event: "Created",
        at: row.created_at,
        by: row.created_by_name,
        reason: row.rationale,
      },
    ];

    if (isGenuineEdit(row)) {
      entries.push({
        id: `${row.id}-modified`,
        milestoneLabel: row.label,
        event: "Last modified",
        at: row.last_modified_at,
        by: row.last_modified_by_name,
        reason: row.rationale,
      });
    }

    return entries;
  });

  return history.sort((a, b) => (a.at < b.at ? 1 : -1));
}

export function buildChangeHistoryColumns(): ColumnsType<MilestoneHistoryRow> {
  return [
    {
      title: "When",
      dataIndex: "at",
      key: "at",
      width: 180,
      render: (value: string) => (
        <span className="text-[#374151]">{formatTimestamp(value)}</span>
      ),
    },
    {
      title: "Who",
      dataIndex: "by",
      key: "by",
      width: 200,
      render: (value: string | null) => (
        <AvatarWithInitials
          name={value}
          avatarSize={24}
          avatarFontSize={11}
          labelFontSize={13}
        />
      ),
    },
    {
      title: "Milestone",
      dataIndex: "milestoneLabel",
      key: "milestoneLabel",
      width: 260,
    },
    {
      title: "Event",
      dataIndex: "event",
      key: "event",
      width: 150,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (value: string | null) =>
        value ? (
          <span className="text-[13px] text-[#374151]">{value}</span>
        ) : (
          <span className="text-[#8AA0A9]">—</span>
        ),
    },
  ];
}
